import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ChevronLeft, Check, Package, Globe,
  Share2, Building2, Sparkles, Plus, Trash2, X, Loader2, LogOut
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import ImageUpload from '@/components/ImageUpload';

const API = process.env.REACT_APP_BACKEND_URL;
const ONBOARDING_KEY = 'supplier_onboarding_done';

const STEPS = [
  { id: 'welcome',   label: 'Welcome',    icon: Sparkles },
  { id: 'company',   label: 'Company',    icon: Building2 },
  { id: 'products',  label: 'Products',   icon: Package },
  { id: 'coverage',  label: 'Coverage',   icon: Globe },
  { id: 'social',    label: 'Finish',     icon: Share2 },
];

const COUNTRY_PRESETS = [
  { name: 'Turkey',         flag: '🇹🇷', cities: ['Istanbul', 'Ankara', 'Izmir'] },
  { name: 'Germany',        flag: '🇩🇪', cities: ['Frankfurt', 'Munich', 'Düsseldorf'] },
  { name: 'UAE',            flag: '🇦🇪', cities: ['Dubai', 'Abu Dhabi'] },
  { name: 'France',         flag: '🇫🇷', cities: ['Paris', 'Lyon', 'Cannes'] },
  { name: 'Italy',          flag: '🇮🇹', cities: ['Milan', 'Rome', 'Bologna'] },
  { name: 'United Kingdom', flag: '🇬🇧', cities: ['London', 'Birmingham'] },
  { name: 'Netherlands',    flag: '🇳🇱', cities: ['Amsterdam', 'Utrecht'] },
  { name: 'Spain',          flag: '🇪🇸', cities: ['Barcelona', 'Madrid'] },
  { name: 'Saudi Arabia',   flag: '🇸🇦', cities: ['Riyadh', 'Jeddah'] },
  { name: 'Switzerland',    flag: '🇨🇭', cities: ['Basel', 'Geneva'] },
];

const CATEGORY_SUGGESTIONS = [
  'Exhibition Staff Uniforms', 'Custom Signage & Banners', 'Stand Furniture & Fixtures',
  'Promotional Products', 'Audio Visual Equipment', 'Catering & Hospitality',
  'LED & Lighting', 'Flooring Solutions', 'Graphics & Print', 'Security & Wristbands',
  'Event Technology', 'Branded Merchandise', 'Logistics & Freight', 'Cleaning Services',
];

const inputCls = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white transition-shadow focus:shadow-md';
const labelCls = 'block text-sm font-semibold text-gray-700 mb-1.5';

export default function SupplierOnboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const supplierMembership = user?.org_memberships?.find(m => m.role?.startsWith('SUPPLIER'));
  const companyName = supplierMembership?.org_name || '';
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [autoSaved, setAutoSaved] = useState(false);

  const autoSave = async (currentStep) => {
    const token = getToken();
    if (!token) return;
    try {
      let payload = {};
      if (currentStep === 1) {
        payload = { tagline, about, logo_url: logoUrl, social: { website, linkedin, instagram, twitter: '', youtube: '' } };
      } else if (currentStep === 2) {
        payload = { catalog: { categories, items: [] } };
      } else if (currentStep === 3) {
        payload = { coverage: { countries, shipping_note: '' } };
      }
      if (Object.keys(payload).length === 0) return;
      await axios.patch(`${API}/api/supplier/profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    } catch {}
  };

  // Step 2: Company
  const [tagline, setTagline] = useState('');
  const [about, setAbout] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  // Step 3: Products
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState('');

  // Step 4: Coverage
  const [countries, setCountries] = useState([]);

  // Step 5: Social
  const [website, setWebsite] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [instagram, setInstagram] = useState('');

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  // Load existing profile data on mount
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/api/supplier/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const p = r.data?.profile || {};
        if (p.tagline) setTagline(p.tagline);
        if (p.about) setAbout(p.about);
        if (p.logo_url) setLogoUrl(p.logo_url);
        if (p.social?.website) setWebsite(p.social.website);
        if (p.social?.linkedin) setLinkedin(p.social.linkedin);
        if (p.social?.instagram) setInstagram(p.social.instagram);
        const cats = p.catalog?.categories || [];
        if (cats.length > 0) setCategories(cats);
        const terr = p.coverage?.countries || p.territories || [];
        if (terr.length > 0) setCountries(terr);
      }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  const fillTestData = () => {
    setTagline('Premium exhibition materials supplier — trusted by 500+ contractors worldwide');
    setAbout('Eurostand Materials provides high-quality raw materials and components for trade show construction. Our catalog includes aluminum profiles, fabric graphics, LED lighting systems, and modular flooring solutions. We ship to 46 countries with 48-hour delivery across Europe and Middle East.');
    setWebsite('eurostand-materials.nl');
    setLinkedin('linkedin.com/company/eurostand-materials');
    setInstagram('instagram.com/eurostand_materials');
    setCountries(['Netherlands', 'Germany', 'Spain']);
    setCategories(['Aluminum Profiles', 'Fabric Printing', 'LED Lighting', 'Modular Flooring']);
  };

  const skipOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
    navigate('/app/supplier/dashboard');
  };

  const handleNext = async () => {
    if (step === 1 && !tagline.trim()) {
      setError(t('validation.required') + ' — Tagline alanı zorunlu');
      return;
    }
    setError('');
    if (step < STEPS.length - 1) {
      await autoSave(step);
      setStep(s => s + 1);
    } else {
      await handleFinish();
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    setError('');
    try {
      const token = getToken();
      const payload = {
        tagline,
        about,
        logo_url: logoUrl,
        catalog: { categories, items: [] },
        coverage: { countries, shipping_note: '' },
        social: { website, linkedin, instagram, twitter: '', youtube: '' },
      };
      await axios.patch(`${API}/api/supplier/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.setItem(ONBOARDING_KEY, '1');
      localStorage.removeItem(`${ONBOARDING_KEY}_banner_dismissed`);
      navigate('/app/supplier/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleCountry = (preset) => {
    const exists = countries.find(c => c.name === preset.name);
    if (exists) {
      setCountries(prev => prev.filter(c => c.name !== preset.name));
    } else {
      setCountries(prev => [...prev, { flag: preset.flag, name: preset.name, cities: preset.cities, primary: false }]);
    }
  };

  const toggleCategory = (cat) => {
    if (categories.includes(cat)) {
      setCategories(prev => prev.filter(c => c !== cat));
    } else {
      setCategories(prev => [...prev, cat]);
    }
  };

  const addCustomCat = () => {
    const trimmed = newCat.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories(prev => [...prev, trimmed]);
      setNewCat('');
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  // ── Step Renders ──────────────────────────────────────────────────────────
  const stepWelcome = (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto shadow-xl">
        <Sparkles className="h-10 w-10 text-white" />
      </div>
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Welcome to ExpoStand!</h2>
        <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto">
          Let's set up your supplier profile in just a few steps. This will help exhibitors and contractors find you across trade shows worldwide.
        </p>
      </div>

      {/* Social Proof Stats */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-100 max-w-lg mx-auto">
        <p className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-4">Why complete your profile?</p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { stat: '3×', label: 'more inquiries with a complete profile' },
            { stat: '72%', label: 'of exhibitors filter by supplier coverage' },
            { stat: '48h', label: 'avg. first inquiry for verified profiles' },
          ].map(({ stat, label }) => (
            <div key={stat} className="text-center">
              <p className="text-2xl font-black text-purple-700">{stat}</p>
              <p className="text-xs text-gray-500 mt-1 leading-snug">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm max-w-lg mx-auto">
        {[
          { icon: Package, title: 'Showcase Products', desc: 'List your supply categories' },
          { icon: Globe, title: 'Set Coverage', desc: 'Countries you operate in' },
          { icon: Share2, title: 'Get Discovered', desc: 'Connect with exhibitors' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="bg-purple-50 rounded-xl p-4 border border-purple-100">
            <Icon className="h-5 w-5 text-purple-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-800 text-xs">{title}</p>
            <p className="text-gray-500 text-xs mt-0.5">{desc}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Takes about 3 minutes · Skip anytime</p>
        <button type="button" onClick={fillTestData}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-purple-400/50 text-purple-600 text-xs font-semibold hover:bg-purple-50 transition-colors">
          ⚡ Fill Test Data
        </button>
      </div>
    </div>
  );

  const stepCompany = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">About Your Company</h2>
        <p className="text-gray-500 text-sm">This appears on your public profile seen by exhibitors.</p>
      </div>
      <div>
        <label className={labelCls}>Tagline <span className="text-gray-400 font-normal">(1 line pitch)</span></label>
        <input className={inputCls} value={tagline} onChange={e => setTagline(e.target.value)}
          placeholder="e.g. Premium Exhibition Uniforms & Branded Merchandise Since 2010" />
      </div>
      <div>
        <label className={labelCls}>About <span className="text-gray-400 font-normal">(optional)</span></label>
        <textarea className={`${inputCls} resize-none`} rows={7} value={about} onChange={e => setAbout(e.target.value)}
          placeholder="Describe your company, what you supply, and your strengths..." />
      </div>
      <div>
        <ImageUpload
          label="Company Logo"
          hint="PNG, JPG, WEBP — Max 5MB"
          aspectRatio="logo"
          value={logoUrl}
          onChange={setLogoUrl}
          token={getToken()}
          data-testid="supplier-onboarding-logo-upload"
        />
      </div>
    </div>
  );

  const stepProducts = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Product Categories</h2>
        <p className="text-gray-500 text-sm">Select what you supply. Exhibitors filter by category to find you.</p>
      </div>
      {/* Quick select */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Popular Categories</p>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_SUGGESTIONS.map(cat => {
            const selected = categories.includes(cat);
            return (
              <button key={cat} onClick={() => toggleCategory(cat)}
                className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${selected
                  ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-purple-400 hover:text-purple-700'
                }`}>
                {selected && <Check className="h-3 w-3 inline mr-1" />}{cat}
              </button>
            );
          })}
        </div>
      </div>
      {/* Custom category */}
      <div className="flex gap-2">
        <input className={inputCls} value={newCat} onChange={e => setNewCat(e.target.value)}
          placeholder="Add custom category..."
          onKeyDown={e => e.key === 'Enter' && addCustomCat()} />
        <button onClick={addCustomCat} disabled={!newCat.trim()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-xl text-sm font-semibold flex items-center gap-1.5 transition-colors whitespace-nowrap">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      {/* Selected */}
      {categories.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2">Selected ({categories.length})</p>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <span key={cat} className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm text-purple-700 font-medium">
                {cat}
                <button onClick={() => setCategories(p => p.filter(c => c !== cat))} className="text-purple-400 hover:text-purple-700">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const stepCoverage = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Where Do You Operate?</h2>
        <p className="text-gray-500 text-sm">Select the countries you supply to. You can add more later.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {COUNTRY_PRESETS.map(preset => {
          const selected = countries.find(c => c.name === preset.name);
          return (
            <button key={preset.name} onClick={() => toggleCountry(preset)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${selected
                ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:border-purple-400 hover:text-purple-700'
              }`}>
              <span className="text-base">{preset.flag}</span>
              <span className="truncate">{preset.name}</span>
              {selected && <Check className="h-3.5 w-3.5 ml-auto flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {countries.length > 0 && (
        <p className="text-xs text-purple-600 font-medium">{countries.length} {countries.length === 1 ? 'country' : 'countries'} selected</p>
      )}
    </div>
  );

  const stepSocial = (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Almost Done!</h2>
        <p className="text-gray-500 text-sm">Add your web presence so exhibitors can learn more about you.</p>
      </div>
      <div>
        <label className={labelCls}>Website</label>
        <input className={inputCls} value={website} onChange={e => setWebsite(e.target.value)}
          placeholder="https://yourcompany.com" />
      </div>
      <div>
        <label className={labelCls}>LinkedIn Company Page <span className="text-gray-400 font-normal">(optional)</span></label>
        <input className={inputCls} value={linkedin} onChange={e => setLinkedin(e.target.value)}
          placeholder="https://linkedin.com/company/..." />
      </div>
      <div>
        <label className={labelCls}>Instagram <span className="text-gray-400 font-normal">(optional)</span></label>
        <input className={inputCls} value={instagram} onChange={e => setInstagram(e.target.value)}
          placeholder="https://instagram.com/..." />
      </div>
      {/* Summary */}
      <div className="bg-purple-50 rounded-xl p-4 border border-purple-100 space-y-2">
        <p className="text-sm font-bold text-purple-800 mb-2">Your Profile Summary</p>
        {[
          { label: 'Tagline', value: tagline || '—', ok: Boolean(tagline) },
          { label: 'About', value: about ? 'Added' : '—', ok: Boolean(about) },
          { label: 'Products', value: categories.length > 0 ? `${categories.length} categories` : '—', ok: categories.length > 0 },
          { label: 'Coverage', value: countries.length > 0 ? `${countries.length} countries` : '—', ok: countries.length > 0 },
          { label: 'Website', value: website || '—', ok: Boolean(website) },
        ].map(({ label, value, ok }) => (
          <div key={label} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{label}</span>
            <span className={`font-medium ${ok ? 'text-emerald-600' : 'text-gray-400'}`}>
              {ok && <Check className="h-3.5 w-3.5 inline mr-1" />}{value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return stepWelcome;
      case 1: return stepCompany;
      case 2: return stepProducts;
      case 3: return stepCoverage;
      case 4: return stepSocial;
      default: return null;
    }
  };

  const isLastStep = step === STEPS.length - 1;
  const isFirstStep = step === 0;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-col w-80 xl:w-96 bg-gradient-to-b from-purple-900 to-purple-700 p-8 text-white flex-shrink-0">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <Package className="h-4 w-4" />
          </div>
          <span className="font-bold text-lg">ExpoStand</span>
          <span className="text-purple-300 text-sm ml-1">Supplier</span>
        </div>

        {/* Steps indicator */}
        <div className="space-y-3 flex-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const active = i === step;
            return (
              <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${active ? 'bg-white/15' : 'opacity-60'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                  done ? 'bg-emerald-400 border-emerald-400' : active ? 'bg-white/20 border-white' : 'border-white/40'
                }`}>
                  {done ? <Check className="h-4 w-4 text-white" /> : <Icon className="h-4 w-4" />}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${active ? 'text-white' : 'text-white/80'}`}>{s.label}</p>
                  {active && <p className="text-xs text-purple-300">Current step</p>}
                  {done && <p className="text-xs text-emerald-300">Completed</p>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Progress */}
        <div className="mt-8">
          <div className="flex justify-between text-xs text-purple-300 mb-2">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* User info */}
        <div className="mt-6 pt-5 border-t border-white/20">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0 text-white font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{companyName || 'Şirket Adı'}</p>
              <p className="text-purple-200 text-xs truncate">{user?.name || '—'}</p>
              <p className="text-purple-300 text-xs truncate">{user?.email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex flex-col">
        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between px-10 py-3.5 border-b bg-white shadow-sm">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-purple-600 flex items-center justify-center">
              <Package className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 text-sm">ExpoStand</span>
            <span className="text-gray-400 text-sm">·</span>
            <span className="text-purple-600 text-sm font-medium">Supplier Onboarding</span>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            data-testid="onboarding-signout-btn"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-purple-600 flex items-center justify-center">
              <Package className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-bold text-gray-900">ExpoStand Supplier</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={skipOnboarding} className="text-xs text-gray-400 hover:text-gray-600">Skip</button>
            <button onClick={logout} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-600">
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>

        {/* Mobile Progress */}
        <div className="lg:hidden px-4 pt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Step {step + 1} of {STEPS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div className="bg-purple-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-lg">
            {renderStep()}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="p-6 lg:px-12 border-t bg-white">
          <div className="max-w-lg mx-auto flex items-center justify-end gap-4">
            <div className="flex gap-3 ml-auto">
              {!isFirstStep && (
                <Button variant="outline" onClick={() => setStep(s => s - 1)} className="gap-2" data-testid="onboarding-back-btn">
                  <ChevronLeft className="h-4 w-4" /> Back
                </Button>
              )}
              {isFirstStep && (
                <Button variant="outline" onClick={skipOnboarding} className="text-gray-500" data-testid="onboarding-skip-btn">
                  Skip setup
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2 min-w-[120px]"
                data-testid="onboarding-next-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
                ) : isLastStep ? (
                  <><Check className="h-4 w-4" /> Complete Setup</>
                ) : (
                  <>Continue <ChevronRight className="h-4 w-4" /></>
                )}
              </Button>
              {autoSaved && (
                <span className="text-xs text-purple-600 font-medium flex items-center gap-1" data-testid="autosave-indicator">
                  <Check className="h-3 w-3" /> Kaydedildi
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
